import { ArrowRight, ArrowRightLeft, RefreshCw } from 'lucide-react';
import type { MigrationJob } from '../../types/modpackMigration';
import { MIGRATION_EXCLUSIONS, migrationBytes, migrationStateLabel } from '../../utils/modpackMigration';
import { SectionHeader } from '../ui/SectionHeader';
import { MigrationRuntimeScope } from './MigrationRuntimeScope';
import { MigrationRuntimeReview } from './MigrationRuntimeReview';
import { MigrationStorageReview } from './MigrationStorageReview';

/** The frozen request stays visible while setup controls make way for the live run. */
export function MigrationRequestSummary({ job, selected, onSelect, onRefresh }: {
  job: MigrationJob; selected: string | null; onSelect: (id: string) => void; onRefresh: () => void;
}) {
  const request = job.request;
  return <section className="subcard p-5 space-y-3" aria-label="Current migration request">
    <SectionHeader title={job.amp_name} icon={ArrowRightLeft} description={job.source_server}
      actions={<button type="button" className="glass-button-sm" onClick={onRefresh} aria-label="Refresh migration inspection"><RefreshCw className="w-4 h-4" /></button>} />
    <div className="flex flex-wrap items-center gap-2 text-sm"><strong className="text-white">{job.source_machine}</strong><ArrowRight className="w-4 h-4 text-[var(--text-muted)]" /><strong className="text-white">{job.destination_machine}</strong><span className="badge badge-neutral ml-auto">{migrationStateLabel(job.state)}</span></div>
    {job.id !== selected && <button type="button" className="glass-button" onClick={() => onSelect(job.id)}>Open current migration</button>}
    <details className="text-xs rounded-lg border border-glass-200/10 p-3">
      <summary className="cursor-pointer text-[var(--text-secondary)]">Request details</summary>
      <div className="space-y-3 mt-3">
        <dl className="grid sm:grid-cols-2 gap-3">
          <Detail label="Source instance" value={job.plan?.source_path ?? request?.source_instance_path ?? 'Not reported'} />
          <Detail label="Destination instance" value={job.plan?.destination_path ?? request?.destination_instance_path ?? 'Not reported'} />
          <Detail label="Future backups" value={job.plan?.destination_backup_path ?? request?.backup_storage_path ?? 'Not reported'} />
          <Detail label="Transfer estimate" value={migrationBytes(job.plan?.transfer_bytes ?? request?.transfer_bytes)} />
        </dl>
        <p className="text-[var(--text-muted)]">Backup archives do not transfer. The destination starts without backups. Accepting the migration deletes source archives.</p>
        {request?.memory_override && <p className="text-amber-200">Memory override reason: {request.memory_override}</p>}
        <MigrationRuntimeReview runtime={job.plan?.runtime ?? request?.runtime} />
        <MigrationStorageReview storage={job.plan?.storage} required={request?.storage?.proof_required} />
        <MigrationRuntimeScope excluded={job.plan?.runtime_exclusions ?? request?.runtime_exclusions ?? MIGRATION_EXCLUSIONS} />
      </div>
    </details>
  </section>;
}

function Detail({ label, value }: { label: string; value: string }) {
  return <div className="min-w-0"><dt className="text-[var(--text-muted)] mb-1">{label}</dt><dd className="text-[var(--text-primary)] break-all">{value}</dd></div>;
}
